export type KrMetadataFieldKey = 'title' | 'h1' | 'description';

export type KrRewriteOperationType =
  | 'element-rewrite'
  | 'section-add'
  | 'section-split'
  | 'heading-add'
  | 'body-span-rewrite';

export const KR_STRUCTURAL_OPERATION_TYPES: ReadonlySet<KrRewriteOperationType> = new Set<KrRewriteOperationType>([
  'section-add',
  'section-split',
  'heading-add',
]);

export function isStructuralKrOperation(operationType?: string | null): boolean {
  return KR_STRUCTURAL_OPERATION_TYPES.has(String(operationType || '') as KrRewriteOperationType);
}

/** Soft batch-gate codes a rewrite can be flagged with while staying applied. */
export type KrRewriteWarningCode =
  | 'batch_editorial_judge'
  | 'batch_repetitive_phrase'
  | 'batch_keyword_grafting'
  | 'batch_no_page_lift'
  | 'sibling_redundant'
  | 'aggregate_no_lift';

/**
 * Present on ACCEPTED report rewrites / `keyword_boost_element` events when a
 * soft batch gate flagged the applied rewrite for human review. The rewrite
 * stays in the draft; the UI shows a review chip plus the existing one-click
 * Reject affordance. `code` is typed loosely so new backend codes degrade to
 * the generic "Review" label instead of breaking the FE.
 */
export interface KrRewriteWarning {
  code: KrRewriteWarningCode | string;
  message: string;
}

export interface KrMetadataFieldPolicy {
  allowedFields: KrMetadataFieldKey[];
  lockedFields: KrMetadataFieldKey[];
  reasons: Partial<Record<KrMetadataFieldKey, string>>;
}

export interface KrNonTextBlocker {
  tagGroup: string;
  customer: number;
  competitor: number;
  gap: number;
  estimatedKasLift: number;
  reason: string;
  actionHint: string;
}

export type KrTagGroupBeatability =
  | 'auto_copy_beatable'
  | 'auto_metadata_beatable'
  | 'auto_heading_beatable'
  | 'auto_section_beatable'
  | 'auto_alt_text_beatable'
  | 'requires_internal_links'
  | 'requires_structured_data'
  | 'requires_better_images'
  | 'keyword_intent_mismatch'
  | 'already_near_ceiling'
  | 'not_enough_supported_content';

export interface KrIntentActionPlanItem {
  keyword: string;
  tagGroup: string;
  customer: number;
  competitor: number;
  gap: number;
  estimatedKasLift: number;
  beatability: KrTagGroupBeatability;
  preferredAction: string;
  canAutoApply: boolean;
  reason: string;
  nextStep: string;
  competitorEvidence?: Array<{
    url?: string;
    headingTag?: string;
    heading?: string;
    title?: string;
    description?: string;
    text?: string;
  }>;
}

export interface KrIntentActionPlan {
  generatedAt?: string;
  items: KrIntentActionPlanItem[];
  summary?: string;
  autoBeatableCount?: number;
  actionOnlyCount?: number;
  noSafeActionCount?: number;
}

export type KrHeadingOutlineActionType = 'rename_heading' | 'add_heading' | 'add_section' | 'no_safe_action';

export type KrHeadingOutlineConfidence = 'full_competitor_outline' | 'limited_competitor_metadata';

export interface KrHeadingOutlineAction {
  actionType: KrHeadingOutlineActionType;
  keyword: string;
  tagGroup: 'headings';
  headingLevel?: 'H2' | 'H3' | 'H4';
  existingElementId?: string | number;
  currentHeading?: string;
  proposedHeading?: string;
  insertBeforeElementId?: string | number;
  parentHeadingId?: string | number | null;
  coveredElementIds?: Array<string | number>;
  reason: string;
  nextStep: string;
  confidence: KrHeadingOutlineConfidence;
  competitorEvidence?: Array<{
    url?: string;
    headingTag?: string;
    heading?: string;
    title?: string;
    description?: string;
    text?: string;
  }>;
  supportEvidence?: Array<{
    elementId?: string | number;
    tag?: string;
    text: string;
  }>;
}

export interface KrHeadingOutlinePlan {
  generatedAt?: string;
  actions: KrHeadingOutlineAction[];
  summary?: string;
  confidence?: KrHeadingOutlineConfidence;
  actionCounts?: Record<KrHeadingOutlineActionType, number>;
  provenance?: {
    keywordCount?: number;
    currentHeadingCount?: number;
    competitorEvidenceCount?: number;
    usedFullCompetitorOutline?: boolean;
  };
}

export interface KrInputFreshnessIssue {
  code: string;
  severity: 'info' | 'warning' | 'blocking';
  message: string;
  expected?: string;
  actual?: string;
}

export interface KrInputFreshnessAudit {
  checkedAt: string;
  inputPageUrl: string;
  resolvedPageUrl?: string;
  canonicalUrl?: string;
  ogUrl?: string;
  contentHash?: string;
  metadataHash?: string;
  metadataFromElements?: Partial<Record<KrMetadataFieldKey, string>>;
  metadataFromSourceHtml?: Partial<Record<KrMetadataFieldKey, string>>;
  issues: KrInputFreshnessIssue[];
  isStale: boolean;
  shouldBlock: boolean;
}

export interface KrBuildFingerprint {
  gitSha?: string;
  buildId?: string;
  deployedAt?: string;
  nodeEnv?: string;
  serviceVersion?: string;
  source?: 'ci_env' | 'package_json' | 'runtime_fallback';
  featureFlags?: Record<string, string | boolean | number>;
}

export interface KrDecisionPlanRecommendation {
  route?: string;
  shouldGenerate?: boolean;
  selectedStrategy?: string;
  stopReason?: string;
  outcomeStatus?: string;
  reason?: string;
}

export interface KrCurrentRoutingResult {
  route?: string;
  selectedStrategy?: string;
  stopReason?: string;
  outcomeStatus?: string;
  acceptedRewriteCount?: number;
  acceptedVisibleRewriteCount?: number;
  baselineProjectedKas?: number;
  finalProjectedKas?: number;
  rawKasDelta?: number;
  officialKasDelta?: number;
}

export interface KrKeywordFitWeightedBlocker {
  keyword: string;
  kis?: number;
  tagGroup: string;
  customer?: number;
  competitor?: number;
  gap?: number;
  weightedContribution?: number;
  blockerType?: string;
  reason?: string;
  fitScore?: number;
  includeInMetadata?: boolean;
  includeInRewrites?: boolean;
}

export interface KrDecisionPlan {
  // `enforcing` is reserved for later backend phases; Phase 1 emits report_only.
  mode?: 'report_only' | 'enforcing';
  status?: 'available' | 'legacy_fallback' | 'error';
  errorReason?: string;
  inputTrust?: KrInputFreshnessAudit;
  keywordFit?: {
    status?: string;
    source?: string;
    totalKeywordCount?: number;
    totalKis?: number;
    safeMetadataKeywordCount?: number;
    safeMetadataKisShare?: number;
    safeRewriteKeywordCount?: number;
    safeRewriteKisShare?: number;
    poorFitKeywordCount?: number;
    poorFitKisShare?: number;
    poorFitWeightedGapShare?: number;
    unsafeOpportunityWeightedShare?: number;
    lowMetadataFitWeightedGapShare?: number;
    nonTextWeightedGapShare?: number;
    topWeightedBlockers?: KrKeywordFitWeightedBlocker[];
    decision?: string;
    reason?: string;
    thresholds?: Record<string, number>;
    topMismatchKeywords?: Array<{
      keyword: string;
      kis?: number;
      fitScore?: number;
      weightedRelevanceGap?: number;
      includeInMetadata?: boolean;
      includeInRewrites?: boolean;
      reason?: string;
      suggestedKeyword?: string;
    }>;
    suggestedReplacements?: Array<{
      keyword: string;
      suggestedKeyword: string;
      reason?: string;
    }>;
    evidence?: string[];
  };
  writableOpportunity?: {
    status?: string;
    decision?: string;
    selectedGroups?: string[];
    primaryAction?: string;
    primaryTagGroup?: string;
    expectedReachableLift?: number;
    expectedAdvisoryLift?: number;
    hasDirectWritableOpportunity?: boolean;
    hasStrongDirectBodyOrHeadingOpportunity?: boolean;
    directReachableTagGroups?: string[];
    partialReachableTagGroups?: string[];
    advisoryTagGroups?: string[];
    nonTextWorkflowNeeds?: KrNonTextBlocker[];
    tagGroups?: Array<{
      tagGroup: string;
      customer?: number;
      competitor?: number;
      gap?: number;
      estimatedKasLift?: number;
      reachability?: string;
      recommendedActions?: string[];
      selected?: boolean;
    }>;
    reason?: string;
    evidence?: string[];
  };
  supportedConcepts?: Array<{
    status?: string;
    reason?: string;
  }>;
  candidateStrategies?: Array<{
    strategy?: string;
    status?: string;
    reason?: string;
  }>;
  decisionPlanRecommendation?: KrDecisionPlanRecommendation;
  currentRoutingResult?: KrCurrentRoutingResult;
  agreement?: {
    routeMatched?: boolean;
    stopReasonMatched?: boolean;
    selectedStrategyMatched?: boolean;
    acceptedCountDelta?: number;
    officialKasDeltaDelta?: number;
    notes?: string[];
  };
  stopPrediction?: {
    stopReason?: string;
    confidence?: number;
    reason?: string;
  };
  warnings?: string[];
}

export interface KrAggregateNoLiftRescueSummary {
  attempted: number;
  accepted: number;
  rejected: number;
  candidates: Array<{
    keyword: string;
    parentHeadingText?: string;
    elementIds: Array<string | number>;
    reason: string;
    targetBodyDelta?: number;
    overallBodyDelta?: number;
    kasDelta?: number;
    weightedBreakdownDelta?: number;
  }>;
}

export interface KrMetadataFluencyIssueSummary {
  total: number;
  byReason: Record<string, number>;
  samples: Array<{
    reason: string;
    source: string;
    fields?: KrMetadataFieldKey[];
    elementTag?: string;
    elementId?: string | number;
    originalSnippet?: string;
    proposedSnippet?: string;
  }>;
}

export interface KrAcceptedSlateQuality {
  evaluated: boolean;
  usedLlm: boolean;
  ok: boolean;
  reason: string;
  editorialRisk: 'low' | 'medium' | 'high' | 'unknown';
  prunedElementIds: Array<string | number>;
  warnings: string[];
  phraseCounts: Record<string, number>;
  durationMs?: number;
  failureReason?: string;
  rawResponse?: string;
}

export interface KrFinalPruningSummary {
  locallyAcceptedRewriteCount: number;
  locallyAcceptedVisibleRewriteCount: number;
  surfacedAcceptedRewriteCount: number;
  surfacedAcceptedVisibleRewriteCount: number;
  prunedRewriteCount: number;
  prunedVisibleRewriteCount: number;
  noLiftPruningApplied: boolean;
  pageKasDeltaBeforePruning: number;
  pageKasDeltaAfterPruning?: number;
  reasons: Record<string, number>;
  prunedElementIds: Array<string | number>;
  supersededRewriteCount?: number;
  supersededElementIds?: Array<string | number>;
  evidence: string[];
}

export interface KrStructurePreservationSummary {
  structuralProfileCounts: Record<string, number>;
  applicationModeCounts: Record<string, number>;
  structuralDemotions: Array<{
    elementId: string | number;
    elementTag: string;
    tagGroup: string;
    operationType: string;
    reason: string;
  }>;
  evidence: string[];
}

export interface KrPhraseSaturationSummary {
  acceptedPhraseCounts: Record<string, number>;
  attemptedPhraseCounts: Record<string, number>;
  recentPrunedPhraseCounts: Record<string, number>;
  saturatedPhrases: Array<{
    phrase: string;
    acceptedCount: number;
    attemptedCount: number;
    prunedCount: number;
    totalCount: number;
    reason: string;
  }>;
  repetitionPreventedCount: number;
}

export interface KrEarlyNoLiftStopSummary {
  triggered: boolean;
  reason?: string;
  evidence: string[];
}

export interface KrNonTextGapAttributionItem {
  tagGroup: string;
  customer: number;
  competitor: number;
  gap: number;
  estimatedLift: number;
  requiredWorkflow: 'schema' | 'image_alt' | 'internal_links' | 'other';
  reason: string;
}

export interface KrCompetitorGapPlanItem {
  keyword: string;
  tagGroup: string;
  customer: number;
  competitor: number;
  gap: number;
  preferredOperation: string;
  missingConceptHints: string[];
  supportedCustomerSectionHints: string[];
  protectedPagePromises: string[];
  allowedMetadataFields: KrMetadataFieldKey[];
  lockedMetadataFields: KrMetadataFieldKey[];
  unsafeOperations: string[];
  confidence: number;
  reason: string;
  source: 'deterministic' | 'llm';
}

export interface KrCompetitorGapPlan {
  generatedAt?: string;
  usedLlm: boolean;
  fallbackReason?: string;
  durationMs?: number;
  metadataFieldPolicy?: KrMetadataFieldPolicy;
  items: KrCompetitorGapPlanItem[];
  nonTextBlockers: KrNonTextBlocker[];
  warnings?: string[];
}

export interface KrRolePreflightSummary {
  evaluated: number;
  skipped: number;
  byRole: Record<string, number>;
  byReason: Record<string, number>;
  samples?: Array<{
    action: 'passed' | 'skipped';
    role: string;
    reason: string;
    elementTag?: string;
    elementId?: string | number;
    textSnippet?: string;
  }>;
}

export interface KrOpportunityCensus {
  rolePreflight?: KrRolePreflightSummary;
  nonTextBlockers?: KrNonTextBlocker[];
  debugNotes?: string[];
}
